import { Validators } from '@angular/forms';
import { GetAssetResponse } from '@assetaccountant/api/models/assets/get-asset.response';
import { pickLatestDate } from '@assetaccountant/core/utils/date-utils';
import {
  emptyValue,
  TypedFormControl,
  TypedFormGroup,
} from '@assetaccountant/core/utils/typed-forms';
import { SubscriptionLike } from 'rxjs';

/**
 * Earliest date the user can record a partial disposal at. Partial disposals modify both the
 * asset's quantity and the per-regime written-down value, so the form must be constrained by
 * the latest of `minimumDates.quantity` and every regime entry in `minimumDates.writtenDownValue`.
 */
export const getPartialDisposalMinimumDate = (asset: GetAssetResponse): string | null =>
  pickLatestDate(
    asset.minimumDates?.quantity,
    ...Object.values(asset.minimumDates?.writtenDownValue ?? {}),
  );

export interface IDisposeQuantityModal {
  asset: GetAssetResponse;

  form: TypedFormGroup<{
    date: TypedFormControl<string>;
    disposedQuantity: TypedFormControl<number>;
    originalQuantity: TypedFormControl<number>;
    quantityUnits: TypedFormControl<string>;
  }>;

  subscriptions: SubscriptionLike[];
}

export const disposeQuantityModalHelper = {
  initForm: (component: IDisposeQuantityModal) => {
    component.form.controls.date.setValue(
      getPartialDisposalMinimumDate(component.asset) ?? emptyValue<string>(),
    );

    if (component.asset.quantity) {
      component.form.controls.originalQuantity.setValue(component.asset.quantity);
      component.form.controls.quantityUnits.setValue(
        component.asset.quantityUnits || emptyValue<string>(),
      );

      component.form.controls.disposedQuantity.setValidators([
        Validators.required,
        Validators.min(0.0001),
        Validators.max(component.asset.quantity),
      ]);
    } else {
      component.subscriptions.push(
        component.form.controls.originalQuantity.valueChanges.subscribe(max => {
          component.form.controls.disposedQuantity.setValidators([
            Validators.required,
            Validators.min(0.0001),
            Validators.max(max),
          ]);
          component.form.controls.disposedQuantity.updateValueAndValidity();
        }),
      );
    }
  },
  toggleOriginalQuantity: (component: IDisposeQuantityModal) => {
    if (component.asset.quantity) {
      component.form.controls.originalQuantity.disable();
      component.form.controls.quantityUnits.disable();
    } else {
      component.form.controls.originalQuantity.enable();
      component.form.controls.quantityUnits.enable();
    }
  },
};

export const QUANTITY_UNITS = [
  {
    label: 'Metric',
    units: [
      { value: 'mm', name: 'Millimetres' },
      { value: 'm', name: 'Metres' },
      { value: 'm²', name: 'Square Metres' },
      { value: 'ha', name: 'Hectares' },
    ],
  },
  {
    label: 'Imperial',
    units: [
      { value: 'ft', name: 'Feet' },
      { value: 'sq ft', name: 'Square Feet' },
      { value: 'ac', name: 'Acres' },
    ],
  },
];
